import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  KeyboardAvoidingView,
  ScrollView,
  TouchableOpacity,
} from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import { ContactType } from '@components/Invite/Contact';
import ContactList from '@components/Invite/ContactList';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { CountBadgeWeb } from '@components/Modals/CountBadge.web';
import { Box, ShadowBox, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import { AssigneeRowSkeleton } from '@components/shared/skeletons/AssigneeRowSkeleton';
import {
  Chat,
  ListMessagesDocument,
  Task,
  TaskMember,
  useAssignTaskUsersMutation,
  useGetChatQuery,
  useGetTaskQuery,
  useUpdateOnboardingStateMutation,
  User,
} from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import useMe from '@hooks/useMe';
import { useMemberAssignedToast } from '@hooks/useMemberAssignedToast';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';
import { logError } from '@utils/logNonFatalError';
import { defaultAssignMemberTaskRole } from '@utils/tasks';
import { sourceCardAssignVariables } from '@utils/tasks/assignTaskSourceCard';

interface MyTagModalProps {
  chatId: Chat['id'];
  assignedTaskId: Task['id'];
  onSave?: () => void;
}

export const ChatAssignToTaskModal: React.FC<MyTagModalProps> = ({
  chatId,
  assignedTaskId,
  onSave,
}) => {
  const { t: tModels } = useTranslation('models');
  const {
    setIsAssigningTaskId,
    setIsAssigningTaskStep2,
    assignTaskSourceCard,
    setAssignTaskSourceCard,
  } = useChatInput();
  const { me } = useMe();
  const showMemberAssignedToast = useMemberAssignedToast();

  const { navigateToTask } = useAppNavigation();
  const [filterVal, setFilterVal] = useState('');
  const [showError, setShowError] = useState('');

  const { data: taskData, refetch: refetchTask } = useGetTaskQuery({
    variables: {
      id: assignedTaskId,
    },
    skip: !assignedTaskId,
  });

  const { data: chatData, loading: chatDataLoading } = useGetChatQuery({
    fetchPolicy: 'cache-and-network',
    variables: { id: chatId },
    skip: !chatId,
  });

  const [selected, setSelected] = useState<ContactType[]>([]);
  const [search, setSearch] = useState<string>('');
  const task = taskData?.getTask;
  const members: TaskMember[] = (task?.members ?? []) as TaskMember[];
  // TA-10891 #23: in a DM the other person is the only possible assignee, so
  // they're pre-selected (unless already on the task) and Confirm is enabled
  // straight away. Applied once per open so a manual deselect sticks.
  const autoAssigneeAppliedRef = useRef(false);
  useEffect(() => {
    if (!task?.members || autoAssigneeAppliedRef.current) return;
    const taskMemberContacts = convertUsersToContacts(
      members.map((m) => m.user)
    );
    const chat = chatData?.getChat;
    const otherChatUsers = ((chat?.users ?? []) as User[]).filter(
      (u) => u.id !== me?.id
    );
    const dmPartner =
      chat && !chat.isGroupChat && otherChatUsers.length === 1
        ? otherChatUsers[0]
        : undefined;
    if (dmPartner) {
      autoAssigneeAppliedRef.current = true;
      const alreadyOnTask = members.some((m) => m.user.id === dmPartner.id);
      setSelected(
        alreadyOnTask
          ? taskMemberContacts
          : [...taskMemberContacts, ...convertUsersToContacts([dmPartner])]
      );
      return;
    }
    setSelected(taskMemberContacts);
  }, [task?.members, chatData?.getChat, me?.id]);

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const [updateOnboardingState] = useUpdateOnboardingStateMutation();

  useEffect(() => {
    if (!me?.id) return;
    const tooltipKey = `assign_task_tooltip_shown_3_${me.id}`;

    globalThis.sessionStorage?.removeItem(tooltipKey);
  }, [me?.id]);

  const [
    assignTaskUsers,
    {
      loading: assignTaskLoading,
      data: assignTaskData,
      error: assignTaskError,
    },
  ] = useAssignTaskUsersMutation({
    refetchQueries: [{ query: ListMessagesDocument, variables: { chatId } }],
    // Code review fix: assignTaskError (below) already drives setShowError —
    // opt out of the global PERMISSION_DENIED/CONFLICT toast so this
    // doesn't double up.
    context: { suppressGlobalMutationErrorToast: true },
  });

  useEffect(() => {
    if (!assignTaskData) return;
    setIsAssigningTaskId(undefined);
    setIsAssigningTaskStep2(false);
    setAssignTaskSourceCard(undefined);
    onSaveRef.current?.();
  }, [assignTaskData]);

  useEffect(() => {
    if (!assignTaskError) return;
    setShowError(assignTaskError.message);
  }, [assignTaskError]);

  const assignTask = async () => {
    try {
      // Existing members must be filtered out — re-sending one would
      // reassign them (an Owner as Assignee). A partially-cached task has no
      // `members`, so fetch them before deciding.
      const taskMembers =
        task?.members ??
        (await refetchTask().catch(() => undefined))?.data?.getTask?.members;
      if (!taskMembers) {
        logError({
          message: 'Assign to task: task members unavailable',
          errorMessage: 'getTask returned no members',
          operationName: 'getTask',
        });
        setShowError(tModels('shared:actionFailedTryAgain'));
        return;
      }
      const selectedUserIds = selected
        .filter((u) => !taskMembers.some((m) => m.user.id == u.id))
        .map((u) => {
          return { userId: u.id, role: defaultAssignMemberTaskRole };
        });

      assignedTaskId &&
        chatId &&
        assignTaskUsers({
          variables: {
            id: assignedTaskId,
            members: selectedUserIds,
            chatId,
            ...sourceCardAssignVariables({
              sourceCard: assignTaskSourceCard,
              chatId,
              taskProjectId: task?.projectId,
              isGroupChat: chatData?.getChat?.isGroupChat,
            }),
          },
          onError: (e) => {
            console.log('Err assignTaskUsers', e);
          },
          onCompleted: () => {
            // Mirror the task-list "Member Assigned" toast, but only in group
            // chats — in a DM the assignment is self-evident from the thread.
            if (chatData?.getChat?.isGroupChat)
              showMemberAssignedToast(task?.name ?? '');
            if (me?.onboardingState?.inviteMemberComplete === false) {
              updateOnboardingState({
                variables: { attributes: { inviteMemberComplete: true } },
                update: (cache) => {
                  if (!me?.id) return;
                  cache.modify({
                    id: cache.identify({ __typename: 'User', id: me.id }),
                    fields: {
                      onboardingState: (existing) => ({
                        ...existing,
                        inviteMemberComplete: true,
                      }),
                    },
                  });
                },
              });
            }
          },
        });
    } catch (err) {
      console.log('err', err);
    }
  };

  const areArraysEqual = (a: string[], b: string[]) => {
    if (a?.length !== b?.length) return false;
    const fun = (x: string, y: string) => x.localeCompare(y);
    const sortedA = [...a].sort(fun);
    const sortedB = [...b].sort(fun);
    return sortedA.every((val, i) => val === sortedB[i]);
  };

  const isSameWithTaskMembers = areArraysEqual(
    members?.map((m) => m.user.id) ?? [],
    selected.map((u) => u.id) ?? []
  );

  const disableConfirm = isSameWithTaskMembers;

  const chatMembers = convertUsersToContacts(
    (chatData?.getChat?.users ?? []) as User[]
  );
  // TA-10279: `chatData`'s `loading` was previously dropped entirely — the
  // Chat Members list rendered empty (no spinner, no skeleton) for the
  // whole first fetch. 150ms minimum hold per the global skeleton rule.
  const showChatMembersSkeleton = useMinimumVisible(
    chatDataLoading && chatMembers.length === 0
  );
  return (
    <>
      {showError && (
        <ConfirmModal
          showModal={showError !== ''}
          isAlert={true}
          onPress={() => {
            setShowError('');
          }}
          buttonText='OK'
          title={tModels('shared:error')}
          message={showError}
        />
      )}
      <ShadowBox
        accessibilityLabel='Tag modal'
        backgroundColor='white'
        flex={1}
        borderRadius='m'
        borderWidth={1}
        borderColor='grey01'>
        <Box mx='xs'>
          <Text mx='xs' mt='m' variant='labelLarge' color='black' mb='xxs'>
            {tModels('tasks.modalTitle.title_assignToTask')}
          </Text>
          {task && (
            <Box flex={1}>
              <TouchableOpacity
                onPress={() => {
                  navigateToTask(task);
                }}>
                <Text mx='xs' variant='webSmall' color='greenSecondary'>
                  #{task?.name}
                </Text>
              </TouchableOpacity>
            </Box>
          )}
        </Box>

        <Box>
          <Box
            paddingHorizontal='m'
            paddingBottom='m'
            my='m'
            borderBottomWidth={1}
            borderBottomColor='grey02'>
            <SearchInput
              placeHolder={tModels('tasks.searchMembers')}
              searchIcon='Users'
              onTextChanged={(v) => {
                setSearch(v);
                setFilterVal(v);
              }}
              value={filterVal}
              autoFocus
              isTagSearch={false}
              isSearchingChatMember={true}
              isChatRelatedTagSearch={true}
            />
          </Box>

          {!!selected.length && (
            <Box>
              <Box flexDirection='row' mb='m' mx='m'>
                <Text
                  variant='labelEmphasizedChatDetialTitle'
                  color='textPrimary'
                  mr='xxs'>
                  {tModels('chat.selectedMember')}
                </Text>
                <CountBadgeWeb count={selected.length.toString()} />
              </Box>

              <ScrollView>
                <FlatList
                  style={{
                    paddingBottom: theme.spacing.xxs,
                  }}
                  horizontal
                  data={selected}
                  ItemSeparatorComponent={() => <Box marginRight='m' />}
                  ListHeaderComponent={() => <Box marginRight='m' />}
                  ListFooterComponent={() => <Box marginRight='m' />}
                  renderItem={({ item }) => (
                    <Box
                      key={item.id}
                      alignItems='center'
                      alignSelf='flex-start'
                      paddingVertical='xxs'>
                      <Avatar
                        size='large'
                        // TA-10891 #22: initials at 22 (heading2), not 32.
                        labelVariant='heading2'
                        label={item.name}
                        avatar={item.avatar}
                        onDismiss={
                          task?.members?.some((t) => t.user.id === item.id)
                            ? undefined
                            : () => {
                                setSelected(
                                  selected.filter((u) => u.id != item.id)
                                );
                              }
                        }
                      />
                      <Text
                        variant='metadata'
                        marginTop='xs'
                        style={{ maxWidth: 64 }}
                        numberOfLines={1}>
                        {item.firstName}
                      </Text>
                    </Box>
                  )}
                />
              </ScrollView>
            </Box>
          )}
        </Box>

        <KeyboardAvoidingView style={{ flex: 1 }}>
          <Box flex={1}>
            <Box paddingVertical='xs' paddingHorizontal='m'>
              <Box flexDirection='row' mb='xxs'>
                <Text variant='labelEmphasized' mr='xxs'>
                  {tModels('tasks.modalListLabel.label', {
                    context: 'chatMembers',
                  })}
                </Text>
                <CountBadgeWeb count={chatMembers.length.toString()} />
              </Box>
              <Text variant='webBodyHintSubline' color='grey04'>
                {tModels('tasks.chooseTheMemberFirst')}
              </Text>
            </Box>
            <Box flex={1}>
              <ContactList
                isSelectedBoxOnRight={true}
                avatarSize='medium'
                disabledUsersId={members?.map((m) => m.user.id)}
                contacts={chatMembers}
                filterVal={search}
                headerSpace='none'
                footerSpace={theme.spacing.l}
                isEditing
                loading={showChatMembersSkeleton}
                showLoadingSkeleton
                skeletonRow={() => <AssigneeRowSkeleton />}
                selected={selected.map((s) => s.id)}
                onPress={(i: ContactType) => {
                  if (task?.members?.some((t) => t.user.id === i.id)) return;
                  if (selected.some((u) => u.id === i.id)) {
                    setSelected(selected.filter((u) => u.id !== i.id));
                  } else {
                    setSelected([...selected, i]);
                  }
                }}
              />
            </Box>
          </Box>
        </KeyboardAvoidingView>

        <Box flexDirection='row' mb='m' mx='m'>
          <Box flex={1}>
            <Button
              borderColor='black'
              backgroundColor='white'
              variant='create'
              isSmall={true}
              fullWidth={true}
              height={48}
              // TA-10279: lock Cancel too while the mutation is in flight —
              // "lock the entire form while the submission spinner runs".
              disabled={assignTaskLoading}
              onPress={() => {
                setIsAssigningTaskStep2(false);
                setIsAssigningTaskId(undefined);
              }}>
              <Text
                variant='labelSmall'
                color={assignTaskLoading ? 'grey04' : 'black'}>
                {tModels('shared:cancel')}
              </Text>
            </Button>
          </Box>

          <Box flex={1}>
            <Button
              marginLeft='m'
              fullWidth={true}
              borderColor='black'
              backgroundColor='black'
              variant='create'
              isSmall={true}
              disabled={disableConfirm}
              // TA-10279: previously folded into `disableConfirm` (a grey,
              // "dead" button) with a separate spinner floating at the top
              // of the modal, above the title — replaced with the shared
              // Button's own isLoading, which keeps the resting colour and
              // swaps the label for a spinner in place.
              isLoading={assignTaskLoading}
              height={48}
              onPress={assignTask}
              accessibilityLabel={tModels('tasks.buttons.confirm')}>
              <Text
                variant='labelSmall'
                color={disableConfirm ? 'grey04' : 'white'}>
                {tModels('tasks.buttons.confirm')}
              </Text>
            </Button>
          </Box>
        </Box>
      </ShadowBox>
    </>
  );
};
